/**
 * How a URL and a language map onto each other, and the cookie that remembers
 * an explicit choice.
 *
 * Shared because both halves must give the same answer. The browser builds the
 * language switcher's links from these helpers; the server decides from them
 * whether an arriving visitor should be sent to the other tree. Two
 * implementations would eventually disagree, and the symptom of that
 * disagreement is a redirect loop.
 *
 * Keep it free of runtime dependencies and of anything Node-specific — it is
 * bundled into the browser.
 */
import { DEFAULT_LOCALE, isLocale, type Locale } from './waitlist.js';

/**
 * The cookie that remembers a language.
 *
 * It is written only when the visitor clicks the language switcher, never from
 * detection. That distinction is the whole legal basis for having no cookie
 * banner: a cookie storing a preference the visitor actively expressed is a
 * "user-interface customisation" cookie, which the ePrivacy Directive exempts
 * from consent. A cookie storing a *guess* would not be exempt.
 *
 * See src/client/legal/cookies.en.tsx, which has to keep saying the same thing.
 */
export const LANGUAGE_COOKIE = 'lang';

/**
 * Pages whose Spanish address is in Spanish, not the English one under /es.
 *
 * Most pages keep one path in both languages (/privacy, /es/privacy). The
 * guides are read for what they are called — someone in Mexico searches
 * "presupuesto quincenal", not "budget by paycheck" — so the Spanish ones
 * live at /es/guias/presupuesto-quincenal. Every move between the languages
 * goes through stripLocale and pathInLocale (the header's switch, the
 * server's redirect, canonicals and hreflang), so the two addresses are
 * always each other's.
 */
export const SPANISH_PATHS: Readonly<Record<string, string>> = {
  '/guides': '/guias',
  '/guides/budget-by-paycheck': '/guias/presupuesto-quincenal',
  '/guides/statement-and-due-dates': '/guias/fecha-de-corte-y-fecha-limite-de-pago',
  '/guides/interest-free-instalments': '/guias/meses-sin-intereses',
};
const FROM_SPANISH = new Map(Object.entries(SPANISH_PATHS).map(([base, es]) => [es, base]));

/** A year — long enough that the choice survives, short enough that it lapses. */
export const LANGUAGE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** Read the locale out of a pathname. Exported so the router and tests agree. */
export function localeFromPath(pathname: string): Locale {
  const first = pathname.split('/').filter(Boolean)[0];
  return isLocale(first) ? first : DEFAULT_LOCALE;
}

/** Strip the locale segment, giving the path as it would be in English. */
export function stripLocale(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const spanish = segments.length > 0 && segments[0] === 'es';
  if (segments.length > 0 && isLocale(segments[0])) segments.shift();
  const base = '/' + segments.join('/');
  // A guide's Spanish address names the same page as its English one.
  return spanish ? (FROM_SPANISH.get(base) ?? base) : base;
}

/** Build the equivalent URL in another language — used by the language switcher. */
export function pathInLocale(pathname: string, locale: Locale): string {
  const base = stripLocale(pathname);
  if (locale === DEFAULT_LOCALE) return base === '/' ? '/' : base;
  return base === '/' ? '/es' : `/es${SPANISH_PATHS[base] ?? base}`;
}

/**
 * The visitor's preferred language, from the `Accept-Language` header the
 * browser sends on its own — nothing is stored to learn this.
 *
 * Returns the first language we actually publish, in the visitor's own order of
 * preference, so someone who reads French then Spanish then English gets
 * Spanish. `null` means the header named nothing we speak, and the caller
 * should fall back to English rather than guess.
 */
export function preferredLocale(header: string | undefined | null): Locale | null {
  if (!header) return null;

  const ranked = header
    .split(',')
    .map((part) => {
      const [tag = '', ...params] = part.trim().split(';');
      const weight = params.find((param) => param.trim().startsWith('q='));
      // A malformed q= is worth less than no q= at all, so treat it as zero.
      const quality = weight ? Number.parseFloat(weight.trim().slice(2)) : 1;
      return { tag: tag.trim().toLowerCase(), quality: Number.isFinite(quality) ? quality : 0 };
    })
    .filter((entry) => entry.tag.length > 0 && entry.quality > 0)
    // A stable sort keeps equal weights in the order the visitor listed them,
    // which is exactly what the specification says they mean.
    .sort((a, b) => b.quality - a.quality);

  for (const { tag } of ranked) {
    // "*" means "anything"; it expresses no preference, so it decides nothing.
    if (tag === '*') return null;
    const base = tag.split('-')[0];
    if (isLocale(base)) return base;
  }

  return null;
}
